import { getHnLanguageLocaleFromString } from 'src/util/portalLanguage-utils';

import type { HNSkjemaFrontend } from '../types/Resources';

import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { getUserLanguage } from '@helsenorge/framework-utils/hn-language';
import { createResourcesApi, getModifiedResourcesForTest, type ResourceItem } from '@helsenorge/framework-utils/resources';

import defaultResources from './HN.Skjema.Frontend.nb-NO.json';

const defaultResourceProject = 'HN.Skjema.Frontend';
const errorCodesResourceProject = 'HN.Skjema.ErrorCodes';
const referoResourceProject = 'Refero';
const errorCodesLanguages = new Set<LanguageLocales>([
  LanguageLocales.NORWEGIAN,
  LanguageLocales.NORWEGIAN_NYNORSK,
  LanguageLocales.SAMI_NORTHERN,
  LanguageLocales.ENGLISH,
]);
const referoLanguages = errorCodesLanguages;

const getErrorCodesLanguage = (language: LanguageLocales, fallback: LanguageLocales): LanguageLocales => {
  const normalizedFallback = errorCodesLanguages.has(fallback) ? fallback : LanguageLocales.NORWEGIAN;
  return errorCodesLanguages.has(language) ? language : normalizedFallback;
};

const getReferoLanguage = (language: LanguageLocales, fallback: LanguageLocales): LanguageLocales => {
  const normalizedFallback = referoLanguages.has(fallback) ? fallback : LanguageLocales.NORWEGIAN;
  return referoLanguages.has(language) ? language : normalizedFallback;
};

const getFallbackLanguage = (): LanguageLocales => {
  const userLanguage = getUserLanguage();
  return getHnLanguageLocaleFromString(userLanguage) ?? LanguageLocales.NORWEGIAN;
};

const getEffectiveLanguage = (projectName: string, language: LanguageLocales): LanguageLocales => {
  const fallback = getFallbackLanguage();
  if (projectName === errorCodesResourceProject) {
    return getErrorCodesLanguage(language, fallback);
  }
  if (projectName === referoResourceProject) {
    return getReferoLanguage(language, fallback);
  }
  return language;
};

const getBundledResources = (projectName: string, language: LanguageLocales): ResourceItem | undefined => {
  if (projectName === defaultResourceProject && language === LanguageLocales.NORWEGIAN) {
    return defaultResources as unknown as ResourceItem;
  }
};

const fetchResources = async (projectName: string, language: LanguageLocales): Promise<ResourceItem> => {
  const effectiveLanguage = getEffectiveLanguage(projectName, language);
  try {
    const resources: { default: ResourceItem } = await import(`./${projectName}.${effectiveLanguage}.json`);
    if (projectName === defaultResourceProject) {
      return { ...defaultResources, ...resources.default } as unknown as ResourceItem;
    }
    return resources.default;
  } catch (error) {
    throw new Error(`Failed to load resources for ${projectName} in ${effectiveLanguage}: ${error}`);
  }
};

export const { resourcesApi, useResources, useSuspenseResources } = createResourcesApi<ResourceItem>({
  getBundledResources,
  fetchResources,
  fallbackLanguage: LanguageLocales.NORWEGIAN,
  useFallbackForMissingTexts: true,
});

export function getResourcesTestHelper(): HNSkjemaFrontend {
  return getModifiedResourcesForTest(defaultResources) as HNSkjemaFrontend;
}
